import { api, withFallback } from "./api";
import { BLOG_POSTS } from "../data/blog";

/**
 * Blog content.
 *
 * Articles are stored as sanitised HTML with a server-generated table of
 * contents and reading time, so every client renders identical values rather
 * than each parsing the body itself.
 *
 * The bundled fallback posts predate that and hold markdown-ish `content`.
 * `normalise` converts them on the way through, so the article page only ever
 * deals with one shape.
 */
function markdownishToHtml(text = "") {
  return text
    .split("\n\n")
    .map((block) => {
      const trimmed = block.trim();
      if (!trimmed) return "";

      if (trimmed.startsWith("## ")) {
        return `<h2>${trimmed.replace(/^##\s*/, "")}</h2>`;
      }

      if (/^\d+\.\s/.test(trimmed) || trimmed.startsWith("- ")) {
        const ordered = /^\d+\.\s/.test(trimmed);
        const items = trimmed
          .split("\n")
          .map((line) => `<li>${line.replace(/^[-\d.\s]+/, "")}</li>`)
          .join("");
        return ordered ? `<ol>${items}</ol>` : `<ul>${items}</ul>`;
      }

      return `<p>${trimmed}</p>`;
    })
    .join("");
}

function slugifyHeading(text) {
  return text.toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-");
}

function normalise(post) {
  if (!post) return post;
  if (post.contentHtml) return post;

  const html = markdownishToHtml(post.content);
  const toc = (post.content || "")
    .split("\n\n")
    .filter((block) => block.startsWith("## "))
    .map((block) => {
      const text = block.replace(/^##\s*/, "");
      return { id: slugifyHeading(text), text, level: 2 };
    });

  // Match the server's anchor ids so in-page links work in the fallback too.
  const withAnchors = html.replace(
    /<h2>([\s\S]*?)<\/h2>/g,
    (_match, inner) => `<h2 id="${slugifyHeading(inner)}">${inner}</h2>`
  );

  return { ...post, contentHtml: withAnchors, toc };
}

export const blogService = {
  list: () => withFallback(() => api.get("/blog"), BLOG_POSTS),

  bySlug: async (slug) => {
    const post = await withFallback(
      () => api.get(`/blog/${slug}`),
      BLOG_POSTS.find((entry) => entry.slug === slug)
    );
    return normalise(post);
  },

  categories: () => withFallback(() => api.get("/blog/categories"), ["All"]),
};
